import React from 'react';
import { BagDetailedIcon, CompassDetailedIcon, EyeIcon, HeartDetailedIcon, SearchIcon } from '@fiverr-private/icons';
import { Stack } from '@fiverr-private/layout_components';
import { Link, Typography } from '@fiverr-private/typography';
import { EXTRACT_PARAM_IDENTIFIER, ICONS_SIZE, PARAMS, PARAMS_MATCHER } from './constants';
import { IconSpacerProps, ParameterizedTextProps } from './ParameterizedText.types';

export const ParameterizedText = ({
    section,
    className,
    color,
    iconColor,
    iconSize = ICONS_SIZE,
    textWrapperProps,
    showOnlyIcon = false,
    width = '100%',
    style,
    marginTop,
}: ParameterizedTextProps): React.JSX.Element | null => {
    const { value, linkUrl } = section;

    if (!value) {
        return null;
    }

    // Split by params existence, but keep them as is in the result array
    const paramsSections = value.split(PARAMS_MATCHER).filter((curr) => !!curr);
    const length = paramsSections.length;

    return (
        <Stack
            className={className}
            gap="0"
            width={width}
            direction="row"
            justifyContent="flexStart"
            alignItems="center"
            style={style}
            marginTop={marginTop}
        >
            {paramsSections.map((curr: string, idx: number) => {
                const [, param] = curr.match(EXTRACT_PARAM_IDENTIFIER) || [];

                // if it's not a param - its considered as regular text
                if (!param) {
                    if (linkUrl) {
                        return (
                            <Link href={linkUrl} key={curr} color={color}>
                                {!showOnlyIcon && <Typography {...textWrapperProps}>{curr}</Typography>}
                            </Link>
                        );
                    }

                    return (
                        <Typography key={curr} color={color} {...textWrapperProps}>
                            {curr}
                        </Typography>
                    );
                } else {
                    let icon: React.JSX.Element;

                    switch (param) {
                        case PARAMS.EYE.identifier:
                            icon = <EyeIcon key={PARAMS.EYE.identifier} size={iconSize} color={iconColor} />;
                            break;
                        case PARAMS.HEART.identifier:
                            icon = <HeartDetailedIcon key={PARAMS.HEART.identifier} size={iconSize} />;
                            break;
                        case PARAMS.BAG.identifier:
                            icon = <BagDetailedIcon key={PARAMS.BAG.identifier} size={iconSize} />;
                            break;
                        case PARAMS.COMPASS.identifier:
                            icon = <CompassDetailedIcon key={PARAMS.COMPASS.identifier} size={iconSize} />;
                            break;
                        case PARAMS.SEARCH.identifier:
                            icon = <SearchIcon key={PARAMS.SEARCH.identifier} size={iconSize} color={iconColor} />;
                            break;
                        default:
                            return null;
                    }

                    if (showOnlyIcon && linkUrl) {
                        return (
                            <IconSpacer key={param} withLeadingSpace={idx !== 0} withTrailingSpace={idx !== length - 1}>
                                <Link href={linkUrl} key={curr} color={color}>
                                    {icon}
                                </Link>
                            </IconSpacer>
                        );
                    }

                    return (
                        <IconSpacer key={param} withLeadingSpace={idx !== 0} withTrailingSpace={idx !== length - 1}>
                            {icon}
                        </IconSpacer>
                    );
                }
            })}
        </Stack>
    );
};

const IconSpacer = ({ children, withLeadingSpace, withTrailingSpace }: IconSpacerProps) => (
    <>
        {withLeadingSpace && <>&nbsp;</>}
        {children}
        {withTrailingSpace && <>&nbsp;</>}
    </>
);

export default ParameterizedText;
